import React, { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useTranslation } from "react-i18next";
import { Formik, Field } from "formik";
import RemoveAssignmentButton from "./RemoveAssignmentButton";
import * as Yup from "yup";

// Custom
import ButtonGroup from "../../components/Buttons/ButtonGroup";
import { Tooltip } from "../../components/Tooltip";
import { useDeleteAndExclude, CANCEL } from "../../hooks/userAssignments";
import moment from "moment";
import { isValidDate, hoursAndMinutesToString } from "../../utils/datetime";
import usePermissions from "../../redux/selectors/usePermissions";
import { DatePickerField } from "../../components/FormikFields";

const useStyles = makeStyles(theme => ({
  input: {
    backgroundColor: theme.palette.common.white,
    height: 40
  }
}));

const getValidationSchema = t => {
  return Yup.object().shape({
    dueDate: Yup.mixed().test(
      "dueDate",
      t("validationMessages.validDate"),
      function() {
        return isValidDate(this.parent.dueDate);
      }
    )
  });
};

const AssignmentDetail = ({
  rowData,
  onSubmit,
  setDates,
  presentDeleteCallback,
  presentUpdateCallback,
  disableUpdating
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { label, call } = useDeleteAndExclude(
    rowData.user.id,
    [rowData],
    presentDeleteCallback,
    presentUpdateCallback
  );
  const [open, setOpen] = useState(false);
  const { notSaveEmployeeAssignments } = usePermissions();

  const AssignmentDetailBtns = ({ submitForm }) => {
    return (
      !notSaveEmployeeAssignments && (
        <ButtonGroup align="right">
          {label &&
            (disableUpdating ? (
              <Tooltip title={t("userAssignments.disableButtonTooltip")}>
                <div>
                  <RemoveAssignmentButton
                    label={label}
                    disabled={disableUpdating}
                    onClick={
                      label === CANCEL ? call : () => setOpen(open => !open)
                    }
                    open={open}
                    setOpen={setOpen}
                    call={call}
                    rowData={rowData}
                  />
                </div>
              </Tooltip>
            ) : (
              <RemoveAssignmentButton
                label={label}
                onClick={label === CANCEL ? call : () => setOpen(open => !open)}
                open={open}
                setOpen={setOpen}
                call={call}
                rowData={rowData}
              />
            ))}
          {disableUpdating ? (
            <Tooltip title={t("userAssignments.disableButtonTooltip")}>
              <div>
                <Button
                  color="primary"
                  variant="contained"
                  disabled={disableUpdating}
                  onClick={submitForm}
                >
                  {t("userAssignments.save")}
                </Button>
              </div>
            </Tooltip>
          ) : (
            <Button color="primary" variant="contained" onClick={submitForm}>
              {t("userAssignments.save")}
            </Button>
          )}
        </ButtonGroup>
      )
    );
  };

  return (
    <Formik
      enableReinitialize={true}
      onSubmit={onSubmit}
      initialValues={{
        dueDate: rowData.dueDate,
        id: rowData.id
      }}
      validationSchema={getValidationSchema(t)}
    >
      {({ submitForm, setFieldValue }) => {
        return (
          <>
            <Box
              justifyContent="space-between"
              display="flex"
              alignItems="flex-end"
              pb={1.75}
            >
              <Box
                display="flex"
                flexDirection="column"
                minWidth={260}
                flex={2}
                ml={13.5}
              >
                <Box display="flex" alignItems="center">
                  <Typography sx={{ flex: "0 0 124px" }} variant="body2">
                    {t("userAssignments.duration")}
                  </Typography>
                  <Typography variant="subtitle2">
                    {hoursAndMinutesToString(
                      rowData.trainingObject.durationHours,
                      rowData.trainingObject.durationMinutes,
                      t
                    )}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" mt={2}>
                  <Typography sx={{ flex: "0 0 124px" }} variant="body2">
                    {t("userAssignments.passingScore")}
                  </Typography>
                  <Typography variant="subtitle2">
                    {`${
                      rowData.passingScore
                        ? `${rowData.passingScore}%`
                        : "Pass/Fail"
                    }`}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" mt={2}>
                  <Typography sx={{ flex: "0 0 124px" }} variant="body2">
                    {t("userAssignments.completedDate")}
                  </Typography>
                  <Box justifyContent="space-between" align="right">
                    <Field
                      name="dueDate"
                      component={DatePickerField}
                      disabled={disableUpdating}
                      onChange={e => {
                        setDates(updatedDates => {
                          updatedDates[rowData.id] = moment(e).format(
                            "YYYY-MM-DD"
                          );
                          return updatedDates;
                        });
                      }}
                      InputClassName={classes.input}
                    />
                  </Box>
                </Box>
              </Box>
              <Box flex={1} px={2} display="flex" justifyContent="center">
                <AssignmentDetailBtns submitForm={submitForm} />
              </Box>
            </Box>
          </>
        );
      }}
    </Formik>
  );
};

export default AssignmentDetail;
